import React, { useState, useEffect } from "react";
import { Collapse } from "@mui/material";
import { isEqual } from "lodash";
import classNames from "classnames";

import { createKeyboardShortcut } from "utils/keyboardShortcuts";
import { getComponentsInStorey, getZoneComponents } from "utils/singleZoneCooling";
import { useStateSelector } from "utils/field";

import StoreyHeaderCounts from "features/Model/Enclosure/StoreyHeaderCounts";
import ZoneActions from "./ZoneActions";
import ZoneStatus from "./ZoneStatus";

import classes from "./style.module.scss";

const ZoneAccordion = React.memo(
    ({
        children,
        className,
        headerClassName,
        bodyClassName,
        id = "",
        label = "",
        change = () => { },
        zoneData = {},
        hasResults = false,
        changesDetected = false,
        warnings = [],
        errors = [],
        formattedMinArea = "",
        forceOpen = false,
        initOpenState = false,
        isEditMode = false,
    }) => {
        const [open, toggleOpen] = useState(initOpenState);

        const containerClasses = [
            classes.accordion,
            open ? classes.open : "",
            className && className,
        ].filter((clss) => clss);

        useEffect(() => {
            if (forceOpen) {
                toggleOpen(true);
            } else {
                toggleOpen(initOpenState);
            }
        }, [forceOpen, initOpenState]);

        const { components = {}, intComponents = {} } = useStateSelector(`model.modelData`) || {};

        const { selectedStorey, selectedRooms = [], selectedWalls = [], measurements = {}, convertedMeasurements = {} } = zoneData;

        const { totalFloorArea = 0, minArea = 0 } = measurements || {};
        const { formattedTotalArea = "", formattedTotalPerimeter = "", formattedTotalVolume = "" } = convertedMeasurements || {};

        const { componentsInStorey, intComponentsInStorey } = getComponentsInStorey(components, intComponents, selectedStorey);
        const zoneComponents = getZoneComponents(componentsInStorey, intComponentsInStorey, selectedRooms, selectedWalls);

        // Update the error for the minimum area
        useEffect(() => {
            if (!isEditMode) return;
            change(`model.modelData.coolingZones.${id}.requirements.hasMinArea`, totalFloorArea >= minArea);
        }, [totalFloorArea, minArea]);

        return (
            <div className={containerClasses.join(" ")}>
                <div
                    className={classNames(classes.header, {
                        [headerClassName]: headerClassName,
                    })}
                    onClick={(e) => {
                        if (e.target.tagName !== "svg") {
                            toggleOpen(!open);
                        }
                    }}
                    {...createKeyboardShortcut({ onKeyDown: () => toggleOpen((prev) => !prev) })}
                >
                    <div className={classes.stat}>
                        <ZoneStatus
                            zoneId={id}
                            changesDetected={changesDetected}
                            hasResults={hasResults}
                            warnings={warnings}
                            errors={errors}
                            formattedMinArea={formattedMinArea}
                        />
                    </div>
                    <div
                        className={classes.heading}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleOpen(!open);
                        }}
                    >
                        <span
                            data-html
                            data-tip={`<span style="display:block;max-width:25rem;">${label}</span>`}
                            data-for={`zone-${id}-label-tip`}
                        >
                            {label}
                        </span>
                    </div>
                    <div className={classes.stat}>
                        <div className={classes.counts}>
                            <StoreyHeaderCounts
                                catId={selectedStorey}
                                showRoomCount
                                overrides={{
                                    roomCount: selectedRooms.length,
                                    componentItems: zoneComponents,
                                }}
                            />
                        </div>
                    </div>
                    <div className={classes.stat}>
                        <div className={classes.summary}>
                            <p
                                data-html
                                data-tip={`Floor Area`}
                                data-for={`zone-${id}-floor-area-tip`}
                            >
                                <span className={classes.summaryLabel}>A:</span> {formattedTotalArea}
                            </p>
                            <p
                                data-html
                                data-tip={formattedTotalPerimeter ? `Wall Perimeter` : `Zone Volume`}
                                data-for={`zone-${id}-perimeter-volume-tip`}
                            >
                                {formattedTotalPerimeter ? (
                                    <p>
                                        <span className={classes.summaryLabel}>P:</span> {formattedTotalPerimeter}
                                    </p>
                                ) : (
                                    <p>
                                        <span className={classes.summaryLabel}>V:</span> {formattedTotalVolume}
                                    </p>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className={classes.hoverActions} onClick={(e) => e.stopPropagation()}>
                        <ZoneActions change={change} zoneId={id} />
                    </div>
                </div>
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    style={{ height: "auto", overflow: "" }}
                >
                    <div className={classNames(classes.body, { [bodyClassName]: bodyClassName })}>{children}</div>
                </Collapse>
            </div>
        );
    },
    isEqual
);

export default ZoneAccordion;
